import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import FlexLib from '@flex/lib';
import PropTypes from 'prop-types';
import { Modal, Box, Button, Checkbox, FormControlLabel, IconButton, Grid, Typography, Switch } from '@mui/material';
import { Close, Textsms, Email } from '@mui/icons-material';
import TableV2, { spreadPaginationOptions } from '../../../components/Table/TableV2';
import { NotificationStatus, PaymentStatus } from '../../../components/Table/components';
import invoicesService from '../../../services/invoicesService';
import DatePickerInput from '../../../components/DatePicker/DatePickerInput';
import { FlexAlerts, AlertSeverityTypes } from '../../../components/Message/Alerts/Alerts';
import messageConstants from '../../../constants/message';
import { emailBulkInvoices, textBulkInvoices, scheduleBulkInvoices } from '../../../actions/RequestFormAction';
import {
  DEFAULT_DAYS_FROM,
  getPaymentStatus,
  PAYMENT_STATUS_MAP,
  getInvoiceFirstName,
  getInvoiceLastName,
} from '../Common';
const { formatCurrency } = FlexLib.utils.feeService;

const ScheduleInvoicesModal = ({ isOpen, onClose, locationId, invoiceLabel }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [invoiceRows, setInvoiceRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationOptions, setPaginationOptions] = useState({});

  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [scheduleDate, setScheduleDate] = useState(new Date());
  const [isScheduled, setIsScheduled] = useState(false);

  const getInvoices = (paginationOptions) => {
    if (locationId && typeof paginationOptions.page === 'number') {
      setIsLoading(true);

      const query = {
        ...spreadPaginationOptions(paginationOptions),
        locationId,
        includeArchived: false,
        pendingOnly: true,
      };
      invoicesService
        .getLocationInvoicesList(query)
        .then((response) => {
          setInvoiceRows(response?.data?.invoices ?? []);
          setRowCount(response?.data?.rowCount ?? 0);
          setMessages([]);
        })
        .catch((error) => {
          setMessages([
            {
              message: `Could not retrieve invoices. ${error?.response?.data?.message ?? ''}`,
              severity: AlertSeverityTypes.error,
            },
          ]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const formattedRows = useMemo(() => {
    return invoiceRows.map((invoice) => {
      return {
        ...invoice,
        id: invoice._id,
        customerFirstName: getInvoiceFirstName(invoice),
        customerLastName: getInvoiceLastName(invoice),
        locationName: invoice.locationId?.locationName,
        paymentStatus: getPaymentStatus(invoice),
      };
    });
  }, [invoiceRows]);

  const handleSelectInvoice = (e, invoice) => {
    if (e?.target?.checked) {
      setSelectedInvoices((prev) => [...prev, invoice]);
    } else {
      setSelectedInvoices((prev) => prev.filter((id) => id._id !== invoice._id));
    }
  };

  const handleSelectAllInvoices = (e) => {
    if (e?.target?.checked) {
      setSelectedInvoices(formattedRows);
    } else {
      setSelectedInvoices([]);
    }
  };

  const handleSendEmailInvoices = () => {
    if (selectedInvoices.length < 1) {
      setMessages([
        {
          message: `No invoices selected.`,
          severity: AlertSeverityTypes.warning,
        },
      ]);
      return;
    }

    setIsLoading(true);
    if (isScheduled) {
      const payload = {
        isEmailBulk: true,
        isTextBulk: false,
        invoices: selectedInvoices,
        scheduledDate: scheduleDate,
      };

      dispatch(scheduleBulkInvoices(payload))
        .then(() => {
          setMessages([
            {
              message: `Invoices have been scheduled.`,
              severity: AlertSeverityTypes.success,
            },
          ]);
        })
        .catch(() => {
          setMessages([
            {
              message: `Invoices could not be scheduled. ${messageConstants.CONTACT_SUPPORT}`,
              severity: AlertSeverityTypes.error,
            },
          ]);
        })
        .finally(() => {
          setIsLoading(false);
          setSelectedInvoices([]);
        });
    } else {
      dispatch(emailBulkInvoices({ selectedRows: selectedInvoices }))
        .then((res) => {
          if (res !== undefined && res.type === 'INVOICE_SUCCESS') {
            setMessages([
              {
                message: `Invoices have been sent out.`,
                severity: AlertSeverityTypes.success,
              },
            ]);
            getInvoices(paginationOptions);
          }
          if (res !== undefined && res.type === 'INVOICE_ERROR') {
            setMessages([
              {
                message: `Invoices could not be sent. ${messageConstants.CONTACT_SUPPORT}`,
                severity: AlertSeverityTypes.error,
              },
            ]);
          }
        })
        .catch(() => {
          setMessages([
            {
              message: `Invoices could not be sent. ${messageConstants.CONTACT_SUPPORT}`,
              severity: AlertSeverityTypes.error,
            },
          ]);
        })
        .finally(() => {
          setIsLoading(false);
          setSelectedInvoices([]);
        });
    }
  };

  const handleSendTextInvoices = () => {
    if (selectedInvoices.length < 1) {
      setMessages([
        {
          message: `No invoices selected.`,
          severity: AlertSeverityTypes.warning,
        },
      ]);
      return;
    }

    setIsLoading(true);
    if (isScheduled) {
      const payload = {
        isEmailBulk: false,
        isTextBulk: true,
        invoices: selectedInvoices,
        scheduledDate: scheduleDate,
      };

      dispatch(scheduleBulkInvoices(payload))
        .then(() => {
          setMessages([
            {
              message: `Invoices have been scheduled.`,
              severity: AlertSeverityTypes.success,
            },
          ]);
        })
        .catch(() => {
          setMessages([
            {
              message: `Invoices could not be scheduled. ${messageConstants.CONTACT_SUPPORT}`,
              severity: AlertSeverityTypes.error,
            },
          ]);
        })
        .finally(() => {
          setIsLoading(false);
          setSelectedInvoices([]);
        });
    } else {
      // legacy method
      let userData = JSON.parse(localStorage.getItem('user'));
      let location = JSON.parse(localStorage.getItem('locationPaydetails'));
      if (location) {
        userData.location = location;
      }
      dispatch(textBulkInvoices({ selectedRows: selectedInvoices, userData: userData }))
        .then((res) => {
          if (res !== undefined && res.type === 'INVOICE_SUCCESS') {
            setMessages([
              {
                message: `Invoices have been sent out.`,
                severity: AlertSeverityTypes.success,
              },
            ]);
            getInvoices(paginationOptions);
          }
          if (res !== undefined && res.type === 'INVOICE_ERROR') {
            setMessages([
              {
                message: `Invoices could not be sent. ${messageConstants.CONTACT_SUPPORT}`,
                severity: AlertSeverityTypes.error,
              },
            ]);
          }
        })
        .catch(() => {
          setMessages([
            {
              message: `Invoices could not be sent. ${messageConstants.CONTACT_SUPPORT}`,
              severity: AlertSeverityTypes.error,
            },
          ]);
        })
        .finally(() => {
          setIsLoading(false);
          setSelectedInvoices([]);
        });
    }
  };

  /**
   * @param {import('../../../../../types/common').PaginationOptions} newPaginationOptions
   */
  const fetchInvoices = (newPaginationOptions) => {
    setPaginationOptions(newPaginationOptions);
    getInvoices(newPaginationOptions);
  };

  const columns = [
    {
      field: 'id',
      type: 'string',
      renderHeader: () => (
        <Checkbox
          onChange={(e) => {
            handleSelectAllInvoices(e);
          }}
        />
      ),
      renderCell: (params) => (
        <Checkbox
          checked={selectedInvoices.some((inv) => inv._id === params.row._id)}
          onChange={(e) => {
            handleSelectInvoice(e, params.row);
          }}
        />
      ),
    },
    {
      field: 'invoice',
      type: 'string',
      headerName: invoiceLabel ?? 'Invoice',
      minWidth: 130,
      flex: 1,
    },
    {
      field: 'customerFirstName',
      type: 'string',
      minWidth: 150,
      flex: 1,
      headerName: 'First Name',
    },
    {
      field: 'customerLastName',
      type: 'string',
      minWidth: 150,
      flex: 1,
      headerName: 'Last Name',
    },
    {
      field: 'amount',
      type: 'number',
      headerName: 'Amount',
      minWidth: 100,
      flex: 1,
      renderCell: (params) => <span>{formatCurrency(params.value)}</span>,
    },
    {
      field: 'locationName',
      type: 'string',
      headerName: 'Location',
      sortable: false,
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'emailSent',
      type: 'boolean',
      minWidth: 115,
      flex: 1,
      headerName: 'Email Status',
      renderCell: (params) => <NotificationStatus isSent={params.value} />,
    },
    {
      field: 'textSent',
      type: 'boolean',
      headerName: 'Text Status',
      renderCell: (params) => <NotificationStatus isSent={params.value} />,
      minWidth: 115,
      flex: 1,
    },
    {
      field: 'paymentStatus',
      type: 'singleSelect',
      valueOptions: PAYMENT_STATUS_MAP,
      headerName: 'Payment Status',
      minWidth: 130,
      flex: 1,
      renderCell: (params) => <PaymentStatus status={params.value} />,
    },
    {
      field: 'dateOpened',
      type: 'date',
      headerName: 'Date',
      minWidth: 100,
      flex: 1,
      valueGetter: (value) => new Date(value),
      renderCell: (params) => <span>{new Date(params.value).toLocaleDateString()}</span>,
    },
  ];

  return (
    <Modal open={isOpen}>
      <Box className="bulkInvoicesModal">
        <Grid container rowSpacing={2} columnSpacing={2} justifyContent="flex-start">
          <Grid item xs={12}>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Grid>
          <Grid item xs={12}>
            <FlexAlerts messages={messages} />
          </Grid>
          <Grid item xs={12}>
            <TableV2
              columns={columns}
              rows={formattedRows}
              fetchData={fetchInvoices}
              rowCount={rowCount}
              isLoading={isLoading}
              dateConfig={{
                isDateFiltering: true,
                defaultFromDateDays: DEFAULT_DAYS_FROM,
              }}
              defaultSortColumn="dateOpened"
              defaultRowsPerPage={5}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="overline">{selectedInvoices.length} invoice(s) selected.</Typography>
          </Grid>

          <Grid item container rowSpacing={2} columnSpacing={2} justifyContent="flex-start">
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={<Switch checked={isScheduled} onChange={(e) => setIsScheduled(e?.target?.checked)} />}
                label="Schedule"
              />
            </Grid>

            {isScheduled && (
              <>
                <Grid item xs={12} md={2}>
                  <DatePickerInput
                    id="schedule-date-picker"
                    format="MM/dd/yyyy"
                    minDate={new Date()}
                    closeOnSelect
                    value={scheduleDate}
                    onChange={(date) => {
                      setScheduleDate(date);
                    }}
                    unstyled
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Grid item xs={12} md={'auto'}>
            <Button variant="contained" startIcon={<Email />} onClick={handleSendEmailInvoices}>
              Email Selected Invoices
            </Button>
          </Grid>
          <Grid item xs={12} md={'auto'}>
            <Button variant="contained" startIcon={<Textsms />} onClick={handleSendTextInvoices}>
              Text Selected Invoices
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

ScheduleInvoicesModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  locationId: PropTypes.string,
  invoiceLabel: PropTypes.string,
};

export default ScheduleInvoicesModal;
